import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as sc from '|/app/header/header.styles';
import { Summary } from '|/app/services';
import theme from '|/components/base/theme';
import Tooltip from '|/components/base/tooltip';
import PurchaseHeaderClockIcon from '|/icons/purchase-header-clock.svg';

const isMoreThanAYearOut = (date: string | Date | null): boolean => {
  if (date === null) return false;
  const inputDate = dayjs(date);
  const oneYearFromNow = dayjs().add(1, 'year');
  return inputDate.isAfter(oneYearFromNow);
};

const PurchaseHeaderClock = styled(PurchaseHeaderClockIcon)`
  padding-right: 4px;
`;

const TooltipTitle = styled.div`
  color: var(--colors-neutral-white);
  ${theme.font.header.xs}
`;

const TooltipContent = styled.span`
  color: var(--colors-neutral-white);
  ${theme.font.body.s}
  white-space: normal;
`;

const TimerDisplay = styled.div`
  display: flex;
  flex-direction: row;
  min-width: 60px;
`;

const Digit = styled.div`
  min-width: 2px;
`;

const formatNumberAsTime = (number: number) => {
  return String(number).padStart(2, '0');
};

const getTimerFormat = (expiresOn: string | null) => {
  if (expiresOn === null) return '00:00:00';
  const now = dayjs();
  const expires = dayjs(expiresOn);

  if (!expires.isValid()) return '00:00:00';

  if (expires.isBefore(now)) return '00:00:00';
  const hours = formatNumberAsTime(expires.diff(now, 'hour'));
  const minutes = formatNumberAsTime(expires.diff(now, 'minute') % 60);
  const seconds = formatNumberAsTime(
    Math.floor(expires.diff(now, 'second') % 60)
  );
  return `${hours}:${minutes}:${seconds}`;
};

const defaultProps = {
  orderSummary: null as null | Summary,
  isModal: false
};

const LockTimer = ({ orderSummary } = defaultProps) => {
  const { vehicle: { unlockDateTime = null, isAvailable = false } = {} } =
    orderSummary || {};
  const isPaused = isMoreThanAYearOut(unlockDateTime);
  const [timerDisplay, setTimerDisplay] = useState(
    getTimerFormat(unlockDateTime)
  );
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      const newTimerDisplay = getTimerFormat(unlockDateTime);
      if (newTimerDisplay !== timerDisplay) setTimerDisplay(newTimerDisplay);
    }, 1000);

    return () => {
      clearTimeout(id);
    };
  }, [timerDisplay, unlockDateTime]);

  if (!isAvailable || isPaused) return null;

  return (
    <sc.TimerWrapper
      onClick={() => setShowTooltip(!showTooltip)}
      data-analytics-id="pp_header_lock_timer"
    >
      <PurchaseHeaderClock />
      <TimerDisplay>
        {timerDisplay.split('').map((digit, index) => (
          <Digit key={index}>{digit}</Digit>
        ))}
      </TimerDisplay>
      {showTooltip && (
        <Tooltip>
          <TooltipTitle>Vehicle is temporarily reserved</TooltipTitle>
          <TooltipContent>
            This vehicle is being held for you while you provide purchase
            details. When time runs out, the vehicle will unlock and could be
            purchased by someone else.
          </TooltipContent>
        </Tooltip>
      )}
    </sc.TimerWrapper>
  );
};

export default LockTimer;
